import type { ScoreGame, ScoreRound } from '@shared/types/game';
import { remainingTricks, scoreRound, trickSumProblem } from '@shared/game-logic';
import { Button, NumberPad, Panel } from '@/components/ui';
import { plural, seatColor, signed } from '@/lib/labels';
import { useScorekeeper } from '@/store/scorekeeper';
import { playCue } from '@/lib/feedback';

/**
 * Entering the tricks each player actually took. Exactly `cards` tricks exist,
 * so the round cannot be closed until the entered counts add up — the single
 * most common scorekeeping mistake, caught before it reaches the standings.
 */
export default function TricksStep({ game, round }: { game: ScoreGame; round: ScoreRound }) {
  const setTricks = useScorekeeper((state) => state.setTricks);
  const confirmRound = useScorekeeper((state) => state.confirmRound);
  const backToBidding = useScorekeeper((state) => state.backToBidding);

  const problem = trickSumProblem(round.cards, round.entries);
  const left = remainingTricks(round.cards, round.entries);

  return (
    <div className="space-y-4">
      <Panel title="Nog te verdelen">
        <p className="text-2xl font-semibold tabular-nums">
          {left}
          <span className="text-base font-normal text-slate-400">
            {' '}
            van {round.cards} {plural(round.cards, 'slag', 'slagen')}
          </span>
        </p>
        {problem === 'too-many' && (
          <p className="mt-1 text-sm text-bad">
            Er zijn {-left} {plural(-left, 'slag', 'slagen')} te veel ingevoerd.
          </p>
        )}
      </Panel>

      {game.seats.map((player) => {
        const entry = round.entries.find((candidate) => candidate.seat === player.seat);
        const bid = entry?.bid ?? 0;
        const tricksWon = entry?.tricksWon ?? null;
        const preview = tricksWon === null ? null : scoreRound(bid, tricksWon, game.config.scoring);

        return (
          <Panel
            key={player.seat}
            title={
              <span className="flex items-center gap-2 normal-case">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: seatColor(player.seat) }}
                />
                <span className="text-base font-semibold tracking-normal text-white">
                  {player.displayName}
                </span>
                <span className="tracking-normal text-slate-400">bood {bid}</span>
              </span>
            }
            action={
              preview !== null && (
                <span
                  className={[
                    'text-base font-semibold tabular-nums',
                    preview >= 0 ? 'text-good' : 'text-bad',
                  ].join(' ')}
                >
                  {signed(preview)}
                </span>
              )
            }
          >
            <NumberPad
              max={round.cards}
              value={tricksWon}
              groupLabel={`${player.displayName} slagen`}
              onSelect={(value) => setTricks(game.id, round.roundNo, player.seat, value)}
            />
          </Panel>
        );
      })}

      <div className="space-y-2">
        <Button
          variant="primary"
          full
          disabled={problem !== null}
          onClick={() => {
            confirmRound(game.id, round.roundNo);
            // The scorekeeper has no websocket, so the cue is played here. Whose
            // result to sound is ambiguous at a shared table, so it reports the
            // round as a whole: exact if everybody called it, missed otherwise.
            const allExact = round.entries.every((entry) => entry.bid === entry.tricksWon);
            playCue({ kind: allExact ? 'bid-exact' : 'bid-missed' });
          }}
        >
          {problem === null
            ? 'Ronde afronden'
            : problem === 'incomplete'
              ? 'Vul iedere speler in'
              : problem === 'too-few'
                ? `Er ${plural(left, 'ontbreekt', 'ontbreken')} nog ${left} ${plural(left, 'slag', 'slagen')}`
                : 'Er zijn te veel slagen ingevoerd'}
        </Button>
        <Button variant="ghost" full onClick={() => backToBidding(game.id, round.roundNo)}>
          Terug naar bieden
        </Button>
      </div>
    </div>
  );
}
